import { useCustom } from "@refinedev/core";
import {
  AlertTriangle,
  ArrowLeft,
  Barcode as BarcodeIcon,
  Check,
  QrCode,
  Search,
  ShieldAlert,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { CityButtons } from "@/components/CityButtons";
import { Combo } from "@/components/Combo";
import { CodeSheet, type CodeRequest } from "@/components/CodeSheet";
import { FindingRow } from "@/components/Finding";
import { MarkAll, MarkBar, MarkCell } from "@/components/Marks";
import { MoveFeed } from "@/components/MoveFeed";
import { SpisanieFeed } from "@/components/SpisanieFeed";
import { ApiError, query } from "@/lib/api";
import {
  boxWord,
  errorMessage,
  findingWord,
  moveWord,
  placeWord,
  spisanieWord,
  stamp,
  taskWord,
  thingWord,
} from "@/lib/format";
import { byCity, cityOptions, placeLabel, supplyView } from "@/lib/places";
import type {
  Barcode,
  BarcodeControl,
  BarcodeHistory,
  BarcodePlace,
  BarcodeSpisanie,
  PlaceSupply,
} from "@/lib/providers";
import {
  click,
  empty,
  lineList,
  picked,
  selectAll,
  type Selection,
} from "@/lib/select";
import { ALL_CAUSES, causeName, causeOptions } from "@/lib/spisanie";
import { useSessionGuard } from "@/lib/useSessionGuard";

const FIELD =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[13px] focus:border-border-strong";

const TH =
  "sticky top-0 z-10 whitespace-nowrap border-b border-border bg-bg-elevated " +
  "px-2.5 py-2 text-[11px] font-medium uppercase tracking-[0.04em] text-fg";

/** Сколько строк ленты показывать сразу и сколько добавляет одно «ещё». */
const PAGE = 10;

/** Потолок ленты на стороне сервиса (`crm/move.py::HISTORY_MAX`). */
const HISTORY_MAX = 200;

/**
 * Сколько мест показывать в списке «где лежал» до первого «ещё».
 *
 * Мест в истории одного размера бывает под три сотни (у худшей карточки склада
 * их 295, замер 09.09.26), и вопрос «когда мы отсюда последний раз брали»
 * закрывается первым десятком: список отсортирован свежими изъятиями вверх.
 */
const PLACES = 15;

/**
 * Сколько списаний этого размера показывать сразу и сколько добавляет «ещё».
 *
 * Десять, а не пятьдесят, как в самом разделе: здесь блок-подсказка под
 * шапкой товара, а не журнал склада. Потолок — тот же, что у сервиса
 * (`repo/spisanie.py::PAGE_MAX`).
 */
const SPISANIE_PAGE = 10;
const SPISANIE_MAX = 200;

/**
 * Раздел «БАРКОД» — где лежит и где ЛЕЖАЛ один размер товара.
 *
 * Вопрос у него свой, не совпадающий с соседями: «Товары» считают модель
 * целиком, «Серия» разбирает одну вещь, «Короба» решают, какой короб везти
 * первым. Сюда приходят с ярлыком в руке и спрашивают про НЕГО — где эти вещи
 * сейчас, из каких коробов они уже уехали и когда мы оттуда брали в последний
 * раз (задача владельца 09.09.26).
 *
 * Три ответа, и они идут в порядке вопросов склада:
 *
 *     где лежит     места с остатком: короб, город, сколько штук
 *     где лежал     места без остатка, свежие изъятия сверху
 *     что было      лента переездов всех вещей этого размера
 *
 * Баркод живёт В АДРЕСЕ (`/barcode/2048334281588`): экран пересылают
 * сменщику ссылкой, и она обязана открываться у него такой же.
 */
export function BarcodePage() {
  const { barcode = "" } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState<CodeRequest | null>(null);
  const [draft, setDraft] = useState(barcode);
  useEffect(() => setDraft(barcode), [barcode]);

  // Место, которым сужена лента внизу. Живёт ЗДЕСЬ, а не внутри списка мест:
  // выбирают его в одном разделе экрана, а отвечает на выбор другой.
  const [pick, setPick] = useState<BarcodePlace | null>(null);
  useEffect(() => setPick(null), [barcode]);
  const historyRef = useRef<HTMLElement>(null);

  /**
   * Щелчок по строке места: сузить ленту этим местом — или снять сужение,
   * если щёлкнули по нему же. Одна и та же строка обязана и ставить, и
   * снимать: иначе снять можно только крестиком, которого в этот момент не
   * видно, — он уехал вниз вместе с лентой.
   */
  const choose = (row: BarcodePlace) => {
    const next = pick && pick.place_id === row.place_id ? null : row;
    setPick(next);
    // Лента стоит под списком мест, и на ДЛИННОМ списке ответ на щелчок
    // оказывается за краем экрана: без прокрутки клик неотличим от промаха.
    //
    // Но прокрутка безусловная дёргала страницу и там, где лента и так на
    // виду, — а вместе с перезагрузкой ленты это читалось как «экран
    // разделился пополам» (замечание владельца 09.09.26, короткий список
    // Ростова). Поэтому едем, ТОЛЬКО если заголовок ленты за краем окна.
    if (next) {
      requestAnimationFrame(() => {
        const seen = historyRef.current?.getBoundingClientRect();
        if (!seen) return;
        // Верх ленты виден и не прижат к самому низу окна — ответ на щелчок
        // человек и так увидит, двигать под ним страницу незачем.
        if (seen.top >= 0 && seen.top <= window.innerHeight - 120) return;
        historyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = draft.trim();
    // Только цифры: `mpArticleBarcode` — bigint, и буквенный набор ушёл бы в
    // 422 от сервиса вместо честного «наберите баркод».
    if (!/^\d+$/.test(next)) return;
    navigate(`/barcode/${next}`);
  };

  const found = useCustom<Barcode>({
    url: `/api/barcode/${encodeURIComponent(barcode)}`,
    method: "get",
    queryOptions: { enabled: /^\d+$/.test(barcode) },
  });

  const loaded = found.result.data;
  // Товаром считается только payload с карточкой: `result.data` у Refine
  // существует и до загрузки — пустым объектом.
  const item = loaded && loaded.card ? loaded : null;
  const error = found.query.error;
  useSessionGuard(error);

  return (
    <main
      className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:p-5"
      data-component="BarcodePage"
      data-annot="баркод"
    >
      <form
        onSubmit={submit}
        data-annot="поиск баркода"
        className="flex flex-wrap items-center gap-2"
      >
        <label className="text-[12.5px] text-fg-muted" htmlFor="barcode-value">
          Баркод
        </label>
        <input
          id="barcode-value"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          inputMode="numeric"
          placeholder="штрихкод с ярлыка"
          className={FIELD + " tnum w-[200px]"}
        />
        <button
          type="submit"
          className="flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] bg-fg px-3 text-[13px] font-medium text-bg"
        >
          <Search size={13} />
          Открыть
        </button>
      </form>

      {!barcode ? (
        <Empty />
      ) : error ? (
        <Failed barcode={barcode} error={error} />
      ) : !item ? (
        <p className="text-[13px] text-fg-muted">Загружаю…</p>
      ) : (
        <>
          <Head item={item} onCode={setCode} />
          {/* Списания стоят СВЕРХУ, сразу под товаром (задача владельца
              11.09.26): «когда и что мы списывали по этому товару» — вопрос,
              с которого начинают разбор пропажи, а не итог, к которому
              приходят, пролистав места и ленту. */}
          <Spisanie barcode={item.barcode} />
          <Control barcode={item.barcode} />
          <Here item={item} onCode={setCode} pick={pick} onPick={choose} />
          <There item={item} onCode={setCode} pick={pick} onPick={choose} />
          <History
            barcode={item.barcode}
            place={pick}
            onClear={() => setPick(null)}
            ref={historyRef}
          />
        </>
      )}

      {code ? (
        <CodeSheet request={code} onClose={() => setCode(null)} />
      ) : null}
    </main>
  );
}

/** Баркод не набран: экран открыли из меню, а не по ссылке. */
function Empty() {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
      Отсканируйте ярлык товара — или наберите баркод цифрами.
    </div>
  );
}

/**
 * Отказ. 404 отделён от прочего намеренно: «такого баркода нет в каталоге»
 * чинится исправленным набором, а недоступный сервис — ожиданием.
 */
function Failed({ barcode, error }: { barcode: string; error: unknown }) {
  const missing = error instanceof ApiError && error.status === 404;
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-3 rounded-[var(--radius-card)] border border-err/60 bg-err-bg p-6 text-center"
    >
      <AlertTriangle size={20} className="text-err" />
      <p className="text-[13.5px]">
        {missing ? `Баркода ${barcode} нет в каталоге` : errorMessage(error)}
      </p>
    </div>
  );
}

/** Что это за вещь и сколько её у нас осталось. */
function Head({
  item,
  onCode,
}: {
  item: Barcode;
  onCode: (code: CodeRequest) => void;
}) {
  const card = item.card;
  return (
    <header className="flex flex-wrap items-start gap-4" data-annot="шапка">
      {card.image_url ? (
        <img
          src={card.image_url}
          alt=""
          referrerPolicy="no-referrer"
          className="h-[132px] w-[96px] flex-none rounded-[var(--radius-ui)] border border-border object-cover"
          onError={(e) => {
            e.currentTarget.style.visibility = "hidden";
          }}
        />
      ) : null}
      <div className="min-w-[240px] flex-1">
        <h1 className="text-[22px] font-semibold tracking-[-0.02em]">
          {card.name || `Карточка ${card.card_id}`}
        </h1>
        {/* Баркод КНОПКОЙ, а не числом: он существует, чтобы его
            сканировали, и Code 128 здесь — тот же, что на карточке товара. */}
        <button
          type="button"
          onClick={() =>
            onCode({
              kind: "barcode",
              value: card.barcode,
              title: `Баркод · размер ${card.size || "—"}`,
            })
          }
          title="Показать штрихкод Code 128"
          data-annot="баркод товара"
          className="mt-2 flex items-center gap-1.5 rounded-[var(--radius-ui)] border border-border px-2 py-1 text-[13px] hover:bg-bg-hover"
        >
          <span className="tnum font-medium">{card.barcode}</span>
          <BarcodeIcon size={14} className="flex-none text-fg-muted" />
        </button>
        <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px]">
          <Pair label="Размер" value={card.size || "—"} />
          <Pair label="Цвет" value={card.color || "—"} />
          <Pair label="Артикул" value={card.article || "—"} mono />
          {/* Штуки в обороте и вещи ЦЕЛИКОМ — разные числа, и второе объясняет
              первое: у 2048334281588 вещей 51, а в обороте девять. Без него
              девять строк остатка читались бы как «всего девять». */}
          <Pair label="В обороте" value={`${item.serials.live} из ${item.serials.total}`} mono />
          <Pair label="Списано" value={String(item.serials.gone)} mono />
        </dl>
        {card.model_id ? (
          <Link
            to={`/products/${card.model_id}`}
            data-annot="к товару"
            className="mt-2 flex w-fit items-center gap-1.5 text-[12.5px] text-fg-muted hover:text-fg"
          >
            <ArrowLeft size={13} className="rotate-180" />
            Все размеры и остатки товара {card.model_id}
          </Link>
        ) : null}
      </div>
    </header>
  );
}

/**
 * Где вещи этого размера лежат СЕЙЧАС — то есть куда за ними идти.
 *
 * ГОРОД отбирается КНОПКАМИ (задача владельца 09.09.26), и первым стоит тот, в
 * котором вещей больше всего: склад у нас в десятке городов, а вопрос «где
 * взять» почти всегда начинается с ближайшего или самого богатого. Число на
 * кнопке — ВЕЩИ, а не места: за ними и едут. Выпадающий список отвечал бы на
 * тот же вопрос двумя движениями вместо одного и прятал бы ответ до первого
 * из них.
 *
 * ЩЕЛЧОК ПО СТРОКЕ сужает ленту движений этим местом — ровно так же, как в
 * списке «где лежал» (замечание владельца 11.09.26). До этого клик работал
 * только во втором списке: считалось, что месту с остатком лента ничего не
 * добавляет. Добавляет, и на складе это первый вопрос после «нашёл»: как эти
 * вещи сюда попали и что ещё через этот короб проходило.
 */
function Here({
  item,
  onCode,
  pick,
  onPick,
}: {
  item: Barcode;
  onCode: (code: CodeRequest) => void;
  pick: BarcodePlace | null;
  onPick: (row: BarcodePlace) => void;
}) {
  const [city, setCity] = useState("");
  useEffect(() => setCity(""), [item.barcode]);

  const cities = useMemo(() => cityOptions(item.places), [item.places]);
  // Город, которого в новом товаре нет, оставил бы пустой список без причины.
  useEffect(() => {
    if (city && !cities.some((c) => c.value === city)) setCity("");
  }, [cities, city]);

  const rows = useMemo(() => byCity(item.places, city), [item.places, city]);
  const units = rows.reduce((n, p) => n + p.units, 0);
  const marks = useMarks(rows, `${item.barcode}:${city}`);

  return (
    <section data-annot="где лежит">
      <h2 className="mb-2 flex flex-wrap items-baseline gap-x-2 text-[15px] font-semibold">
        Где лежит сейчас{" "}
        <span className="font-normal text-fg-muted">
          {units} шт · {rows.length} {placeWord(rows.length)}
          {city && rows.length !== item.places.length
            ? ` из ${item.places.length}`
            : ""}
        </span>
      </h2>

      {/* Кнопки стоят НАД списком, а не в строке заголовка: их бывает десяток
          (у 2041326193104 вещи лежат в восьми городах), и в одну строку с
          числами они не помещаются вовсе. */}
      <div className="mb-2">
        <CityButtons
          options={cities}
          value={city}
          onChange={setCity}
          count={(c) => c.units}
          total={item.units}
          totalLabel="все города"
          hint={(c) =>
            `${c.label}: ${c.units} шт в ${c.places} ${placeWord(c.places)}`
          }
        />
      </div>

      {rows.length === 0 ? (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
          {city
            ? "В этом городе вещей этого размера сейчас нет."
            : "Ни одной вещи этого размера сейчас на складе нет."}
        </div>
      ) : (
        <>
          <CopyBar marks={marks} />
          <PlaceTable
            rows={rows}
            kind="here"
            onCode={onCode}
            picked={pick?.place_id ?? null}
            onPick={onPick}
            marks={marks}
          />
        </>
      )}
    </section>
  );
}

/**
 * Где вещи ЛЕЖАЛИ — места, из которых этот размер уже уехал.
 *
 * Порядок задаёт сервис — по дате последнего изъятия, свежее сверху, — и это
 * весь смысл списка: вопрос «когда мы отсюда последний раз брали» и есть его
 * сортировка.
 *
 * По умолчанию показаны ТОЛЬКО КОРОБА (задача владельца 09.09.26): вопрос
 * задают про хранение, а мест по дороге у вещи много — химчистка, отпаривание,
 * сборка, короба FBS. Переключатель рядом открывает весь маршрут, потому что
 * «где он был» иногда именно про них.
 *
 * ГОРОД сужает список вторым отбором (замечание владельца 09.09.26): склад у
 * нас в трёх с лишним городах, и «где он лежал» почти всегда спрашивают про
 * тот город, в котором стоят. Города считаются из самих строк — второго
 * запроса за ними нет, они уже приехали в каждой.
 *
 * ЩЕЛЧОК ПО СТРОКЕ сужает ленту движений внизу этим местом: список отвечает
 * «где вещь была», лента — «что там происходило», и это две половины одного
 * разбора.
 */
function There({
  item,
  onCode,
  pick,
  onPick,
}: {
  item: Barcode;
  onCode: (code: CodeRequest) => void;
  pick: BarcodePlace | null;
  onPick: (row: BarcodePlace) => void;
}) {
  const [korobOnly, setKorobOnly] = useState(true);
  const [city, setCity] = useState("");
  const [limit, setLimit] = useState(PLACES);
  // Новый баркод — новый список: потолок, город и переключатель, оставшиеся от
  // прошлого товара, к этому отношения не имеют.
  useEffect(() => {
    setKorobOnly(true);
    setCity("");
    setLimit(PLACES);
  }, [item.barcode]);

  // Два отбора независимы, и каждое число на экране считается по своему
  // набору: города — по режиму (сколько коробов в Ростове), переключатель — по
  // городу (сколько всего мест в выбранном городе). Иначе один отбор врал бы
  // про другой.
  const base = useMemo(
    () => (korobOnly ? item.past.filter((p) => p.korob) : item.past),
    [item.past, korobOnly],
  );
  const inCity = useMemo(() => byCity(item.past, city), [item.past, city]);
  const cities = useMemo(() => cityOptions(base), [base]);
  // Город, которого в текущем наборе больше нет (переключили «только короба»),
  // молча оставил бы пустой список с непонятной причиной.
  useEffect(() => {
    if (city && !cities.some((c) => c.value === city)) setCity("");
  }, [cities, city]);

  const rows = korobOnly ? inCity.filter((p) => p.korob) : inCity;
  const shown = rows.slice(0, limit);
  const word = korobOnly ? boxWord : placeWord;
  // Отметка держится на ПОКАЗАННЫХ строках: копировать можно только то, что
  // человек видит, а «показать ещё» отметку переживает — оно список не меняет,
  // а продолжает.
  const marks = useMarks(shown, `${item.barcode}:${city}:${korobOnly}`);

  return (
    <section data-annot="где лежал">
      <h2 className="mb-2 flex flex-wrap items-baseline gap-x-2 text-[15px] font-semibold">
        Где лежал раньше{" "}
        <span className="font-normal text-fg-muted">
          {rows.length} {word(rows.length)}
          {/* Сколько мест отсекает город — иначе непонятно, сузился список
              отбором или их столько и есть. */}
          {city && rows.length !== base.length ? ` из ${base.length}` : ""} ·
          свежие изъятия сверху
        </span>
        <button
          type="button"
          onClick={() => setKorobOnly((now) => !now)}
          data-annot="только короба"
          className="rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-[12px] font-normal hover:bg-bg-hover"
        >
          {korobOnly
            ? `Показать все места (${inCity.length})`
            : `Только короба (${inCity.filter((p) => p.korob).length})`}
        </button>
      </h2>

      {/* Города — те же кнопки, что у списка «где лежит»: два отбора одного
          экрана обязаны и выглядеть, и нажиматься одинаково. Мерятся они здесь
          МЕСТАМИ: вещей ни в одном из этих мест уже нет. */}
      <div className="mb-2" data-annot="город">
        <CityButtons
          options={cities}
          value={city}
          onChange={setCity}
          count={(c) => c.places}
          total={base.length}
          totalLabel="все города"
          hint={(c) => `${c.label}: ${c.places} ${placeWord(c.places)}`}
        />
      </div>

      {shown.length > 0 ? <CopyBar marks={marks} /> : null}

      {shown.length === 0 ? (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
          {city
            ? "В этом городе таких мест нет."
            : korobOnly
              ? "Из коробов этот размер не забирали ни разу."
              : "Мест, откуда этот размер уже уехал, нет."}
        </div>
      ) : (
        <PlaceTable
          rows={shown}
          kind="there"
          onCode={onCode}
          picked={pick?.place_id ?? null}
          onPick={onPick}
          marks={marks}
        />
      )}

      {rows.length > shown.length ? (
        <button
          type="button"
          onClick={() => setLimit((now) => now + PLACES)}
          data-annot="ещё места"
          className="mt-2 h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover"
        >
          {/* «Ещё 5 из 5» — фраза ни о чём: второе число повторяет первое,
              когда хвост короче шага. Остаток называется, только если он
              БОЛЬШЕ того, что даст нажатие. */}
          Ещё {Math.min(PLACES, rows.length - shown.length)}
          {rows.length - shown.length > PLACES
            ? ` · осталось ${rows.length - shown.length}`
            : ""}
        </button>
      ) : null}
    </section>
  );
}

/**
 * Отметка строк списка мест галочками и то, что с отмеченного уносят.
 *
 * Задача владельца 09.09.26: отметить нужные короба и унести их СПИСКОМ — по
 * образцу раздела «Короба», где это уже работает. Хук общий на оба списка
 * мест: отметка, ведущая себя в двух соседних таблицах по-разному, — сбитая
 * привычка руки, а не мелочь интерфейса.
 *
 * `scope` — то, при смене чего отметка сбрасывается: другой товар, другой
 * город, другой режим списка. Отмечали ВИДИМОЕ, и оставить отметку значило бы
 * скопировать короба, которых на экране уже нет. «Показать ещё» в `scope` не
 * входит намеренно: оно список продолжает, а не меняет.
 */
function useMarks(rows: readonly BarcodePlace[], scope: string) {
  const order = useMemo(() => rows.map((r) => r.place_id), [rows]);
  const [sel, setSel] = useState<Selection<number>>(() => empty<number>());
  const [flash, setFlash] = useState("");

  useEffect(() => setSel(empty<number>()), [scope]);
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(""), 3000);
    return () => clearTimeout(t);
  }, [flash]);

  const chosen = picked(rows, (r) => r.place_id, sel);
  const marked = chosen.length;
  const allMarked = marked > 0 && marked === rows.length;

  /**
   * Уносит поле отмеченных строк СТОЛБЦОМ — каждое значение с новой строки
   * (задача владельца 09.09.26). `missing` называет, чего не было у
   * пропущенных строк: «скопировано 8» и «8, а двум нечего копировать» —
   * разные ответы, и второй нужен чаще.
   */
  const copy = async (
    label: string,
    value: (row: BarcodePlace) => string,
    missing: string,
  ) => {
    const { text, count, skipped } = lineList(chosen.map(value));
    if (count === 0) {
      setFlash(`${label}: копировать нечего`);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Буфер закрыт политикой браузера — показываем список, чтобы его можно
      // было выделить руками. Молча терять его нельзя.
      window.prompt("Скопируйте список:", text);
    }
    setFlash(`${label}: ${count}` + (skipped ? ` · ${missing} ${skipped}` : ""));
  };

  return {
    on: (place_id: number) => sel.keys.has(place_id),
    marked,
    allMarked,
    flash,
    copy,
    pick: (
      key: number,
      e: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean },
    ) =>
      // Shift, Ctrl и ⌘ здесь означают одно: «от той строки до этой» — как в
      // разделе «Короба». На складе жмут то, что первым попало под палец.
      setSel((s) => click(s, order, key, e.shiftKey || e.ctrlKey || e.metaKey)),
    toggleAll: () => setSel(allMarked ? empty<number>() : selectAll(order)),
    clear: () => setSel(empty<number>()),
  };
}

type Marks = ReturnType<typeof useMarks>;

/**
 * Полоса копирования над таблицей мест.
 *
 * Кнопок ДВЕ, потому что у места два разных значения и подменять одно другим
 * нельзя: «номер короба» — то, чем короб зовут глазами (10944), «код места» —
 * то, чем его берут сканером (850900210944). Оба стоят в строке, оба уносят в
 * чужие системы — в наряд кладовщику, в сообщение сменщику, в поставку.
 *
 * Номер берётся ТОЛЬКО у коробов: у зала и стола сборки `sortOrder` один на
 * всех (10000) и смысла не несёт, поэтому такие строки в номера не идут и
 * честно считаются пропущенными.
 */
function CopyBar({ marks }: { marks: Marks }) {
  return (
    <MarkBar
      marked={
        marks.marked > 0
          ? `Отмечено ${marks.marked} ${placeWord(marks.marked)}`
          : ""
      }
      allMarked={marks.allMarked}
      hint="Отметьте места галочками: Shift или Ctrl по второй строке отметит всё между ними."
      flash={marks.flash}
      onAll={marks.toggleAll}
      onNone={marks.clear}
      actions={[
        {
          label: "Скопировать номера коробов",
          title: "Номера отмеченных коробов, каждый с новой строки",
          annot: "скопировать номера",
          onClick: () =>
            marks.copy(
              "Номера коробов",
              (row) => (row.korob ? row.code : ""),
              "не короба",
            ),
        },
        {
          label: "Скопировать коды мест",
          title: "Коды отмеченных мест, каждый с новой строки",
          annot: "скопировать коды",
          onClick: () =>
            marks.copy(
              "Коды мест",
              (row) => row.place_barcode ?? "",
              "без кода места",
            ),
        },
      ]}
    />
  );
}

/**
 * Что списывали ПО ЭТОМУ размеру — и по какой причине.
 *
 * Задача владельца 11.09.26: раздел «Списание» отвечает на «что мы списали»
 * складом целиком, а здесь в руках ярлык — и вопрос свой: «когда и что мы
 * списывали по ЭТОМУ товару». Ответ подсказывает то, чего не видно ни в
 * остатке, ни в ленте: вещь не потерялась, её сняли с учёта, вот когда и вот
 * откуда.
 *
 * ОТБОР ПО УМОЛЧАНИЮ — «Не нашли», тот же, что в разделе: блок про пропажи, а
 * не про отгрузки маркетплейсу (у баркода 2041326193104 их 254 против двух
 * «Не нашли», замер 11.09.26). Причина живёт в АДРЕСЕ (`?cause=`) — как место,
 * которым сужают ленту ниже: экран пересылают сменщику целиком.
 *
 * ПРИЧИНЫ СЧИТАЮТСЯ ПО ЭТОМУ ЖЕ ТОВАРУ: числа в отборе меряют строки, которые
 * он откроет, а не весь склад.
 */
function Spisanie({ barcode }: { barcode: string }) {
  const [params, setParams] = useSearchParams();
  const cause = params.get("cause") ?? "";
  const [limit, setLimit] = useState(SPISANIE_PAGE);
  // Новый товар — новый список: потолок, поднятый на прошлом, к этому
  // отношения не имеет.
  useEffect(() => setLimit(SPISANIE_PAGE), [barcode, cause]);

  const journalQuery = useCustom<BarcodeSpisanie>({
    url: `/api/barcode/${encodeURIComponent(barcode)}/spisanie${query({
      category: cause || undefined,
      n: limit,
    })}`,
    method: "get",
    // Прежние строки стоят, пока едут новые: смена причины иначе схлопывала бы
    // страницу под рукой — та же мина, что ловили в ленте 09.09.26.
    queryOptions: { placeholderData: (prev) => prev },
  });

  const loaded = journalQuery.result.data;
  const journal = loaded && Array.isArray(loaded.rows) ? loaded : null;
  const busy = journalQuery.query.isFetching;
  const error = journalQuery.query.error;

  const pick = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set("cause", value);
    else next.delete("cause");
    setParams(next, { replace: true });
  };

  // Пока ответа нет вовсе — блока нет: пустая рамка «загружаю» под шапкой
  // товара мешала бы читать то, ради чего экран открыт.
  if (!journal && busy) return null;

  if (error) {
    return (
      <p
        data-annot="списания"
        className="flex items-center gap-2 text-[12.5px] text-fg-muted"
      >
        <AlertTriangle size={13} className="flex-none text-warn" />
        Списания: {errorMessage(error)}
      </p>
    );
  }
  if (!journal) return null;

  const categories = journal.categories;
  const chosen = String(journal.category_id);
  const rows = journal.rows;

  return (
    <section data-annot="списания" className="flex flex-col gap-2">
      <h2 className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[15px] font-semibold">
        Списания{" "}
        <span className="font-normal text-fg-muted">
          {busy ? "обновляю…" : `${rows.length} ${spisanieWord(rows.length)}`}
          {!busy && journal.has_more ? " · есть ещё" : ""}
          {" · по "}
          {journal.serials} {thingWord(journal.serials)} этого размера
        </span>
        {/* Отбор — тот же выпадающий список, что в разделе «Списание»: две
            двери к одному вопросу обязаны спрашивать одинаково. Причин у
            одного товара единицы (шесть у 2041326193104), но список общий с
            разделом, где их двадцать девять. */}
        <Combo
          label="Причина"
          value={chosen}
          options={causeOptions(categories, "все причины товара")}
          onChange={pick}
          placeholder="поиск причины"
          width={240}
        />
      </h2>

      {rows.length === 0 ? (
        <p className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4 text-center text-[13px] text-fg-muted">
          {/* Пустой ответ называет ПРИЧИНУ, по которой он пуст: «ни разу по
              этой причине» и «не списывали вовсе» — разные состояния товара. */}
          {chosen === ALL_CAUSES
            ? "Вещи этого размера ещё ни разу не списывали."
            : `По причине «${causeName(categories, chosen)}» этот размер не списывали.`}
        </p>
      ) : (
        <SpisanieFeed
          rows={rows}
          caption="Списания вещей этого баркода: когда, какой серией, откуда и чьей рукой"
          withPlace
          withCategory={chosen === ALL_CAUSES}
        />
      )}

      {journal.has_more && limit < SPISANIE_MAX ? (
        <button
          type="button"
          onClick={() => setLimit((now) => Math.min(now + SPISANIE_PAGE, SPISANIE_MAX))}
          disabled={busy}
          data-annot="ещё списания"
          className="h-8 w-fit rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover disabled:opacity-55"
        >
          {busy ? "Загружаю…" : `Ещё ${SPISANIE_PAGE}`}
        </button>
      ) : null}
    </section>
  );
}

/**
 * Что контроль FBS нашёл ПРО ЭТОТ размер.
 *
 * Задача владельца 09.09.26: раздел «FBS Контроль» отвечает на «сколько всего
 * ошибок накопил склад», и найти в его списке свой товар нечем — находка
 * адресована серией и задачей, а в руках ярлык. Здесь тот же контроль,
 * повёрнутый к товару.
 *
 * ПРАВИЛА ТЕ ЖЕ И ЕДИНСТВЕННЫЕ: дверь сужает вход и выход того же реестра,
 * своей логики контроля у экрана нет. Поэтому и рисуется находка тем же
 * компонентом, что в разделе контроля.
 *
 * Пустой ответ — ОДНА СТРОКА, а не четыре карточки с нулями: раздел здесь не
 * главный, а «расхождений нет» само по себе полный ответ, если рядом сказано,
 * ЧТО проверено. Отказ соседа при этом обязан выглядеть отказом: в песочной
 * полосе базы задач нет вовсе, и молчаливый ноль читался бы как «всё в
 * порядке» ровно там, где не проверено ничего.
 */
function Control({ barcode }: { barcode: string }) {
  const report = useCustom<BarcodeControl>({
    url: `/api/barcode/${encodeURIComponent(barcode)}/control`,
    method: "get",
  });
  const data = report.result.data;
  const checks = data && Array.isArray(data.checks) ? data.checks : null;
  const error = report.query.error;

  if (report.query.isFetching && !checks) return null;

  if (error) {
    return (
      <p
        data-annot="контроль fbs"
        className="flex items-center gap-2 text-[12.5px] text-fg-muted"
      >
        <AlertTriangle size={13} className="flex-none text-warn" />
        Контроль FBS: {errorMessage(error)}
      </p>
    );
  }
  if (!checks || !data) return null;

  const hot = checks.filter((one) => one.found > 0);
  const volume = `проверено ${data.scanned.tasks} ${taskWord(data.scanned.tasks)} по ${data.serials} шт этого размера`;

  if (hot.length === 0) {
    return (
      <p
        data-annot="контроль fbs"
        className="flex items-center gap-2 text-[12.5px] text-fg-muted"
      >
        <Check size={13} className="flex-none text-ok" />
        {/* Объём проверки стоит РЯДОМ с «расхождений нет»: без него эта фраза
            неотличима от «проверить было нечем». */}
        Контроль FBS: расхождений по этому баркоду нет · {volume}
      </p>
    );
  }

  return (
    <section data-annot="контроль fbs" className="flex flex-col gap-2">
      <h2 className="flex flex-wrap items-center gap-2 text-[15px] font-semibold">
        <ShieldAlert size={15} className="flex-none text-err" />
        Контроль FBS{" "}
        <span className="font-normal text-fg-muted">
          {data.found} {findingWord(data.found)} · {volume}
        </span>
      </h2>
      {hot.map((check) => (
        <div
          key={check.code}
          data-annot={`контроль ${check.code}`}
          className="rounded-[var(--radius-ui)] border border-err/50"
        >
          <header className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-1.5">
            <h3 className="text-[13px] font-medium">{check.title}</h3>
            <span className="rounded-full bg-err-bg px-2 py-0.5 text-[11.5px] text-err">
              {check.found} {findingWord(check.found)}
            </span>
            {/* Вопрос правила — то же, что в разделе контроля: без него
                находка читается как приговор без обвинения. */}
            <p className="w-full text-[12px] text-fg-muted">{check.question}</p>
          </header>
          <ul className="divide-y divide-border">
            {check.items.map((item, i) => (
              <FindingRow key={`${check.code}-${item.serial ?? "x"}-${i}`} item={item} />
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

/**
 * Таблица мест. Одна на оба списка, и колонки в ней те же: место, известное
 * только историей, обязано выглядеть так же, как место с остатком, иначе два
 * списка читались бы как два разных склада.
 *
 * Разница ровно в одной колонке: у мест с остатком это «штук» и «положили», у
 * пустых — «забрали в последний раз».
 *
 * `onPick` делает строки КЛИКАБЕЛЬНЫМИ — так список сужает ленту движений
 * выбранным местом. С 11.09.26 он есть у ОБОИХ списков (замечание владельца):
 * вопрос «что через это место проходило» задают и коробу, в котором вещи лежат
 * прямо сейчас.
 *
 * КОЛОНКА «ПОСТАВКА» — ответ на «куда ушёл товар» (задача владельца 11.09.26).
 * Она стоит в обоих списках, потому что нужна в обоих: в истории поставкой
 * помечено большинство коробов (125 мест из 298 на худшей карточке склада), а
 * в остатке она объясняет самый неочевидный случай — короб уехал, а вещи в нём
 * ещё числятся.
 */
function PlaceTable({
  rows,
  kind,
  onCode,
  picked,
  onPick,
  marks,
}: {
  rows: BarcodePlace[];
  kind: "here" | "there";
  onCode: (code: CodeRequest) => void;
  picked?: number | null;
  onPick?: (row: BarcodePlace) => void;
  marks: Marks;
}) {
  const here = kind === "here";
  return (
    <div className="overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
      <table className="w-full border-collapse text-[13px]">
        <caption className="sr-only">
          {here
            ? "Места, где вещи этого баркода лежат сейчас"
            : "Места, откуда вещи этого баркода уже забрали"}
        </caption>
        <thead className="hidden md:table-header-group">
          <tr>
            <th scope="col" className={TH + " w-9 text-center"}>
              <MarkAll
                all={marks.allMarked}
                some={marks.marked > 0}
                title="Отметить все показанные места"
                onToggle={marks.toggleAll}
              />
            </th>
            <th scope="col" className={TH + " text-left"}>
              Место
            </th>
            <th scope="col" className={TH + " text-left"}>
              Город
            </th>
            <th scope="col" className={TH + " text-right"}>
              {here ? "Штук" : "Брали"}
            </th>
            <th scope="col" className={TH + " text-left"}>
              {here ? "Положили" : "Забрали"}
            </th>
            <th scope="col" className={TH + " text-left"}>
              Поставка
            </th>
            <th scope="col" className={TH + " text-left"}>
              Код места
            </th>
          </tr>
        </thead>
        <tbody className="block md:table-row-group">
          {rows.map((row) => (
            <tr
              key={row.place_id}
              data-annot={`место ${row.place_id}`}
              // Строка кликабельна только там, где клику есть чем ответить.
              // `tabIndex` и клавиши идут с ней в паре: сужение ленты — не
              // украшение строки, и добраться до него обязано и с клавиатуры.
              onClick={onPick ? () => onPick(row) : undefined}
              onKeyDown={
                onPick
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onPick(row);
                      }
                    }
                  : undefined
              }
              tabIndex={onPick ? 0 : undefined}
              aria-pressed={onPick ? picked === row.place_id : undefined}
              title={onPick ? "Показать движения через это место" : undefined}
              className={
                "mb-2 block rounded-[var(--radius-ui)] border border-border bg-bg p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:bg-transparent md:p-0 " +
                (onPick ? "cursor-pointer " : "") +
                (picked === row.place_id
                  ? "border-fg/40 bg-bg-elevated md:bg-bg-elevated"
                  : "md:hover:bg-bg-hover")
              }
            >
              {/* Галочка гасит всплытие сама (`MarkCell`): строка списка «где
                  лежал» кликабельна и сужает ленту, и без этого одно нажатие
                  делало бы два дела сразу. */}
              <MarkCell
                label={`Отметить ${placeLabel(row)}`}
                on={marks.on(row.place_id)}
                onPick={(e) => marks.pick(row.place_id, e)}
              />
              <Cell label="Место">
                <span className="flex flex-col">
                  {/* Имя КОРОБА — это его номер, и только он: собственное имя
                      места несёт ещё и склад («Невиномыск Короб 10944»), а
                      город стоит колонкой рядом. Втроём они давали строку, где
                      город и номер повторялись дважды каждый (замечание
                      владельца 09.09.26). */}
                  <span>{placeLabel(row)}</span>
                  {row.group ? (
                    <span className="text-[11px] text-fg-muted">{row.group}</span>
                  ) : null}
                </span>
              </Cell>
              <Cell label="Город">
                <span className="text-fg-muted">{row.city || "—"}</span>
              </Cell>
              <Cell label={here ? "Штук" : "Брали"} align="tnum md:text-right">
                {here ? row.units : `${row.moves_out}×`}
              </Cell>
              <Cell label={here ? "Положили" : "Забрали"} align="tnum whitespace-nowrap">
                {/* У места с остатком «положили» — это `placeDate` самой
                    свежей вещи, а у пустого «забрали» — время последнего
                    переезда наружу. Прочерк там, где события не было. */}
                {stamp(here ? row.placed : row.last_out) || "—"}
              </Cell>
              <Cell label="Поставка">
                <SupplyCell supply={row.supply} />
              </Cell>
              <Cell label="Код места">
                {row.place_barcode ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      // Клик по коду места — про код, а не про строку: без
                      // остановки он заодно сузил бы ленту, и человек получил
                      // бы два действия за одно нажатие.
                      e.stopPropagation();
                      onCode({
                        kind: "qr",
                        value: row.place_barcode as string,
                        title: `Код места · ${row.name || row.place_id}`,
                      });
                    }}
                    title="Показать QR с кодом места"
                    className="flex items-center gap-1.5 rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-[12.5px] hover:bg-bg-hover"
                  >
                    <span className="tnum">{row.place_barcode}</span>
                    <QrCode size={13} className="flex-none text-fg-muted" />
                  </button>
                ) : (
                  <span className="text-fg-muted">—</span>
                )}
              </Cell>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Поставка, за которой числится место: чем её зовут и куда она уехала.
 *
 * Прочерк — самый частый ответ, и он же законный: в поставку отдают только
 * КОРОБА маркетплейса, и только когда их закрыли. У зала, стола сборки и
 * набираемого короба поставки нет и быть не может.
 *
 * Обе строки собирает чистая `supplyView` (`lib/places.ts`), и она же кладёт
 * подробности в подсказку: статус, второй номер ВБ и обе даты порознь. В
 * ячейку они не помещаются, а на вопрос «а точно та поставка?» отвечают.
 */
function SupplyCell({ supply }: { supply: PlaceSupply | null }) {
  if (!supply) return <span className="text-fg-muted">—</span>;

  const view = supplyView(supply);
  return (
    <span className="flex flex-col" title={view.title}>
      <span className="tnum whitespace-nowrap">{view.head}</span>
      {view.tail ? (
        <span className="text-[11px] text-fg-muted">{view.tail}</span>
      ) : null}
    </span>
  );
}

/**
 * Лента движений ВСЕХ вещей этого баркода.
 *
 * Номер серии здесь в каждой строке, и это не украшение: строки от разных
 * вещей, и без номера лента читалась бы как один непрерывный маршрут, которым
 * она не является.
 *
 * «Ещё» поднимает ПОТОЛОК одного запроса, а не склеивает страницы — тот же
 * приём, что в ленте серии: между двумя запросами вещь могут сдвинуть, и
 * склейка показала бы переезд дважды.
 *
 * `place` сужает ленту до одного места — так на щелчок по строке в списке «где
 * лежал» отвечает раздел, который умеет ответить. Сужает ЗАПРОС, а не
 * показанные строки: первых десяти строк ленты место из прошлого месяца не
 * достигает вовсе, и отсев на клиенте показал бы пусто там, где переезды есть.
 */
function History({
  barcode,
  place,
  onClear,
  ref,
}: {
  barcode: string;
  place: BarcodePlace | null;
  onClear: () => void;
  ref?: React.Ref<HTMLElement>;
}) {
  const [limit, setLimit] = useState(PAGE);
  // Новый баркод — новая лента; новое место — тоже: десять строк, набранные
  // по всему размеру, к отбору одного короба отношения не имеют.
  useEffect(() => setLimit(PAGE), [barcode, place?.place_id]);

  const historyQuery = useCustom<BarcodeHistory>({
    url: `/api/barcode/${encodeURIComponent(barcode)}/history${query({
      limit,
      place: place?.place_id,
    })}`,
    method: "get",
    queryOptions: {
      // ПРЕЖНИЕ СТРОКИ ОСТАЮТСЯ, пока едут новые. Без этого лента на время
      // запроса пустела, страница схлопывалась на высоту десяти строк — и
      // экран под рукой уезжал, показывая полполотна пустоты (замечание
      // владельца 09.09.26). Что строки уже не те, говорит «Обновляю…» в
      // заголовке — молчаливая подмена была бы хуже прыжка.
      placeholderData: (prev) => prev,
    },
  });
  const loaded = historyQuery.result.data;
  const moves = loaded && Array.isArray(loaded.moves) ? loaded.moves : [];
  const busy = historyQuery.query.isFetching;
  const error = historyQuery.query.error;

  return (
    <section data-annot="история" ref={ref}>
      <h2 className="mb-2 flex flex-wrap items-baseline gap-x-2 text-[15px] font-semibold">
        История движения{" "}
        <span className="font-normal text-fg-muted">
          {/* Пока едут новые строки, на экране стоят ПРЕЖНИЕ — иначе страница
              схлопывалась бы под рукой. Поэтому «обновляю» говорится вслух:
              подменить строки молча значило бы соврать о том, на какой вопрос
              они отвечают. */}
          {busy ? "обновляю…" : `${moves.length} ${moveWord(moves.length)}`}
          {!busy && loaded?.has_more ? " · есть ещё" : ""}
        </span>
        {/* Отбор назван и снимается ЗДЕСЬ же: лента, суженная местом, внешне
            неотличима от полной, и без этой строки десять переездов одного
            короба читались бы как вся история размера. */}
        {place ? (
          <button
            type="button"
            onClick={onClear}
            data-annot="снять место"
            title="Показать движения по всем местам"
            className="flex items-center gap-1.5 rounded-[var(--radius-ui)] border border-border bg-bg-elevated px-2 py-0.5 text-[12px] font-normal hover:bg-bg-hover"
          >
            через {placeLabel(place)}
            {place.city ? ` · ${place.city}` : ""}
            <X size={12} className="flex-none text-fg-muted" />
          </button>
        ) : null}
      </h2>

      {error ? (
        <div
          role="alert"
          className="rounded-[var(--radius-card)] border border-err/60 bg-err-bg p-4 text-center text-[13px]"
        >
          {errorMessage(error)}
        </div>
      ) : moves.length === 0 ? (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
          {busy
            ? "Загружаю…"
            : place
              ? "Через это место вещи этого размера не проезжали."
              : "Вещи этого размера ещё ни разу не двигали."}
        </div>
      ) : (
        // Таблица ОБЩАЯ на все ленты склада (`components/MoveFeed.tsx`): у
        // серии, баркода и короба один и тот же вопрос к строке — час, оба
        // конца, задача и человек. До 10.09.26 она стояла здесь копией.
        <MoveFeed
          moves={moves}
          tasksKnown={loaded?.tasks_known ?? false}
          withSerial
          caption="Перемещения вещей этого баркода: когда, какой серией, откуда, куда и чьей рукой"
        />
      )}

      {loaded?.has_more && limit < HISTORY_MAX ? (
        <button
          type="button"
          onClick={() => setLimit((now) => Math.min(now + PAGE, HISTORY_MAX))}
          disabled={busy}
          data-annot="ещё"
          className="mt-2 h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover disabled:opacity-55"
        >
          {busy ? "Загружаю…" : `Ещё ${PAGE}`}
        </button>
      ) : null}
    </section>
  );
}

function Pair({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-fg-muted">{label}</dt>
      <dd className={mono ? "tnum" : ""}>{value}</dd>
    </div>
  );
}

/**
 * Ячейка, которая на узком экране становится строкой «подпись — значение».
 * Тот же приём, что в карточке серии и в разделе «Товары».
 */
function Cell({
  label,
  align,
  children,
}: {
  label: string;
  align?: string;
  children: React.ReactNode;
}) {
  return (
    <td
      data-label={label}
      className={
        "flex items-center justify-between gap-3 border-b border-border/60 py-1 last:border-0 " +
        "before:text-[11px] before:uppercase before:tracking-[0.04em] before:text-fg-muted before:content-[attr(data-label)] " +
        "md:table-cell md:border-0 md:px-2.5 md:py-1.5 md:before:content-none " +
        (align ?? "")
      }
    >
      {children}
    </td>
  );
}
